import React, { useState, useEffect } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    LinearProgress,
    IconButton,
    Tooltip,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    useMediaQuery,
    Stack,
    Divider,
    InputAdornment,
    Popover,
} from '@mui/material';
import {
    TrendingUp,
    TrendingDown,
    AttachMoney,
    Receipt,
    Refresh,
    Warning,
    Edit,
    Add,
    PhoneAndroid,
    AccountBalance,
    FilterList,
    Clear,
    CalendarToday,
    ViewList,
    Close,
    Check,
} from '@mui/icons-material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    AreaChart,
    Area,
    ComposedChart,
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import expenseApi from '../api/expenseApi';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];
const SOURCE_COLORS = {
    mpesa: '#4CAF50',
    bank: '#1976d2',
    manual: '#FF9800',
};

const Dashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [loading, setLoading] = useState(true);
    const [statistics, setStatistics] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [budget, setBudget] = useState(0);
    const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
    const [newBudget, setNewBudget] = useState('');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [filteredExpenses, setFilteredExpenses] = useState([]);
    const userId = expenseApi.getCurrentUserId();

    // Date and Limit States
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [customLimit, setCustomLimit] = useState('');
    const [useCustomLimit, setUseCustomLimit] = useState(false);
    const [dateFilterAnchor, setDateFilterAnchor] = useState(null);

    useEffect(() => {
        fetchData();
    }, [userId, startDate, endDate, customLimit]);

    useEffect(() => {
        if (expenses.length > 0) {
            applyFilters();
        }
    }, [sourceFilter, expenses]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const statsResponse = await expenseApi.getStatistics(userId);
            setStatistics(statsResponse.data.data);

            // Build params
            const params = {
                limit: useCustomLimit && customLimit ? parseInt(customLimit) : 10000,
                offset: 0,
            };

            // Add date filters if set
            if (startDate) {
                params.start_date = format(new Date(startDate), 'yyyy-MM-dd');
            }
            if (endDate) {
                params.end_date = format(new Date(endDate), 'yyyy-MM-dd');
            }

            const expensesResponse = await expenseApi.getUserExpenses(userId, params);
            setExpenses(expensesResponse.data.data.expenses || []);
            setFilteredExpenses(expensesResponse.data.data.expenses || []);

            const budgetResponse = await expenseApi.getMonthlyBudget(userId);
            setBudget(budgetResponse.data.data.budget || 0);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        if (sourceFilter === 'all') {
            setFilteredExpenses(expenses);
        } else {
            setFilteredExpenses(expenses.filter(exp => exp.source === sourceFilter));
        }
    };

    const handleSetBudget = async () => {
        if (!newBudget || parseFloat(newBudget) <= 0) {
            toast.error('Please enter a valid budget amount');
            return;
        }

        try {
            await expenseApi.setMonthlyBudget({
                userId,
                budget: parseFloat(newBudget),
            });
            setBudget(parseFloat(newBudget));
            setBudgetDialogOpen(false);
            toast.success('Budget updated successfully');
        } catch (error) {
            console.error('Error setting budget:', error);
            toast.error('Failed to update budget');
        }
    };

    const handleSourceFilterChange = (event) => {
        setSourceFilter(event.target.value);
    };

    const clearFilter = () => {
        setSourceFilter('all');
    };

    const clearDateFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setDateFilterAnchor(null);
    };

    const handleDateFilterOpen = (event) => {
        setDateFilterAnchor(event.currentTarget);
    };

    const handleDateFilterClose = () => {
        setDateFilterAnchor(null);
    };

    const applyDateFilters = () => {
        handleDateFilterClose();
        fetchData();
    };

    // Get source label with icon
    const getSourceLabel = (source) => {
        if (source === 'mpesa') {
            return { label: 'M-Pesa', icon: <PhoneAndroid sx={{ fontSize: 14 }} />, color: '#4CAF50' };
        } else if (source === 'bank') {
            return { label: 'Bank', icon: <AccountBalance sx={{ fontSize: 14 }} />, color: '#1976d2' };
        } else if (source === 'manual') {
            return { label: 'Manual', icon: <Edit sx={{ fontSize: 14 }} />, color: '#FF9800' };
        }
        return { label: 'Unknown', icon: null, color: '#9E9E9E' };
    };

    if (loading || !statistics) {
        return (
            <Box sx={{ width: '100%', mt: 4 }}>
                <LinearProgress />
                <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading dashboard...</Typography>
            </Box>
        );
    }

    // Calculate filtered totals
    const filteredTotal = filteredExpenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
    const filteredCount = filteredExpenses.length;

    // Get source breakdown from statistics
    const sourceBreakdown = statistics.sourceBreakdown || [];

    const { total, categories = [], monthlyTrend = [], expenseCount } = statistics;

    const budgetUtilization = budget > 0 ? (filteredTotal / budget) * 100 : 0;
    const isOverBudget = budgetUtilization > 100;

    // Filter categories based on source
    const filteredCategories = sourceFilter === 'all'
        ? categories
        : categories.filter(cat => cat.source === sourceFilter);

    const pieData = filteredCategories.map(cat => ({
        name: cat.category,
        value: parseFloat(cat.total_amount),
        count: cat.count,
    }));

    // Filter monthly trend based on source
    const filteredTrendData = monthlyTrend.filter(item => {
        if (sourceFilter === 'all') return true;
        return item.source === sourceFilter;
    });

    const trendData = filteredTrendData.reverse().map(item => ({
        month: item.month,
        total: parseFloat(item.total),
    }));

    const recentExpenses = filteredExpenses.slice(0, 5);

    // Source distribution data for chart
    const sourceDistribution = sourceBreakdown.map(item => ({
        name: item.source ? item.source.toUpperCase() : 'Unknown',
        value: parseFloat(item.total_amount) || 0,
        count: item.count || 0,
        color: SOURCE_COLORS[item.source] || '#9E9E9E',
    }));

    const dateFilterOpen = Boolean(dateFilterAnchor);

    return (
        <Box>
            {/* Header with Filters */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: 3,
                gap: 2
            }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Expense Dashboard
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Overview of your expenses
                    </Typography>
                    {(startDate || endDate) && (
                        <Chip
                            size="small"
                            label={`${startDate ? format(new Date(startDate), 'dd MMM yyyy') : 'Any'} - ${endDate ? format(new Date(endDate), 'dd MMM yyyy') : 'Any'}`}
                            onDelete={clearDateFilters}
                            sx={{ mt: 1 }}
                        />
                    )}
                    {useCustomLimit && customLimit && (
                        <Chip
                            size="small"
                            label={`Limit: ${customLimit} transactions`}
                            onDelete={() => { setUseCustomLimit(false); setCustomLimit(''); }}
                            sx={{ mt: 1, ml: 1 }}
                            color="info"
                        />
                    )}
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<CalendarToday />}
                        onClick={handleDateFilterOpen}
                        size="small"
                        sx={{
                            borderColor: startDate || endDate ? '#1976d2' : undefined,
                            color: startDate || endDate ? '#1976d2' : undefined,
                        }}
                    >
                        Date
                        {(startDate || endDate) && (
                            <Chip
                                size="small"
                                label="Active"
                                sx={{ ml: 0.5, height: 16, fontSize: '0.6rem', bgcolor: '#1976d2', color: 'white' }}
                            />
                        )}
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={<ViewList />}
                        onClick={() => setUseCustomLimit(!useCustomLimit)}
                        size="small"
                        sx={{
                            borderColor: useCustomLimit ? '#1976d2' : undefined,
                            color: useCustomLimit ? '#1976d2' : undefined,
                        }}
                    >
                        Limit
                        {useCustomLimit && customLimit && (
                            <Chip
                                size="small"
                                label={customLimit}
                                sx={{ ml: 0.5, height: 16, fontSize: '0.6rem', bgcolor: '#1976d2', color: 'white' }}
                            />
                        )}
                    </Button>

                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Source</InputLabel>
                        <Select
                            value={sourceFilter}
                            onChange={handleSourceFilterChange}
                            label="Source"
                        >
                            <MenuItem value="all">All Sources</MenuItem>
                            <MenuItem value="mpesa">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PhoneAndroid sx={{ fontSize: 18, color: '#4CAF50' }} />
                                    M-Pesa
                                </Box>
                            </MenuItem>
                            <MenuItem value="bank">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountBalance sx={{ fontSize: 18, color: '#1976d2' }} />
                                    Bank
                                </Box>
                            </MenuItem>
                            <MenuItem value="manual">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Edit sx={{ fontSize: 18, color: '#FF9800' }} />
                                    Manual
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                    {sourceFilter !== 'all' && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={clearFilter}
                            startIcon={<Clear />}
                            sx={{ color: '#d32f2f' }}
                        >
                            Clear
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => setBudgetDialogOpen(true)}
                        size="small"
                    >
                        Budget
                    </Button>
                    <IconButton onClick={fetchData} sx={{ color: '#1976d2' }} size="small">
                        <Refresh />
                    </IconButton>
                </Stack>
            </Box>

            {/* Date Filter Popover */}
            <Popover
                open={dateFilterOpen}
                anchorEl={dateFilterAnchor}
                onClose={handleDateFilterClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: { p: 3, minWidth: 300, maxWidth: 400 }
                }}
            >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Filter by Date Range
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate || ''}
                        onChange={(e) => setStartDate(e.target.value || null)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate || ''}
                        onChange={(e) => setEndDate(e.target.value || null)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={clearDateFilters}
                            fullWidth
                            size="small"
                            startIcon={<Clear />}
                        >
                            Clear
                        </Button>
                        <Button
                            variant="contained"
                            onClick={applyDateFilters}
                            fullWidth
                            size="small"
                            startIcon={<Check />}
                            sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
                        >
                            Apply
                        </Button>
                    </Box>
                </Box>
            </Popover>

            {/* Limit Input */}
            {useCustomLimit && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            Transaction Limit:
                        </Typography>
                        <TextField
                            size="small"
                            type="number"
                            placeholder="Enter limit (e.g., 100)"
                            value={customLimit}
                            onChange={(e) => setCustomLimit(e.target.value)}
                            sx={{ width: 150 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setUseCustomLimit(false);
                                                setCustomLimit('');
                                            }}
                                        >
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                                if (customLimit && parseInt(customLimit) > 0) {
                                    fetchData();
                                } else {
                                    toast.error('Please enter a valid limit');
                                }
                            }}
                            sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
                        >
                            Apply Limit
                        </Button>
                        {customLimit && (
                            <Chip
                                size="small"
                                label={`Showing ${customLimit} transactions`}
                                sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
                            />
                        )}
                    </Box>
                </Paper>
            )}

            {/* Source Filter Badge */}
            {sourceFilter !== 'all' && (
                <Chip
                    icon={getSourceLabel(sourceFilter).icon}
                    label={`Viewing: ${getSourceLabel(sourceFilter).label} expenses`}
                    onDelete={clearFilter}
                    sx={{ mb: 2, bgcolor: '#e3f2fd', color: '#1976d2' }}
                />
            )}

            {/* Summary Cards */}
            <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="caption">
                                        Total Expenses
                                    </Typography>
                                    <Typography variant={isMobile ? 'h6' : 'h5'}>
                                        KES {filteredTotal.toLocaleString()}
                                    </Typography>
                                </Box>
                                <AttachMoney sx={{ fontSize: { xs: 30, sm: 40 }, color: '#1976d2' }} />
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                {filteredCount} transactions
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="caption">
                                        Monthly Budget
                                    </Typography>
                                    <Typography variant={isMobile ? 'h6' : 'h5'}>
                                        KES {budget.toLocaleString()}
                                    </Typography>
                                </Box>
                                <Receipt sx={{ fontSize: { xs: 30, sm: 40 }, color: '#1976d2' }} />
                            </Box>
                            <Box sx={{ mt: 1 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(budgetUtilization, 100)}
                                    color={isOverBudget ? 'error' : 'primary'}
                                />
                                <Typography variant="caption" color="textSecondary">
                                    {budgetUtilization.toFixed(1)}% used
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="caption">
                                        Average Per Day
                                    </Typography>
                                    <Typography variant={isMobile ? 'h6' : 'h5'}>
                                        KES {filteredExpenses.length > 0 ? (filteredTotal / 30).toFixed(0) : 0}
                                    </Typography>
                                </Box>
                                <TrendingUp sx={{ fontSize: { xs: 30, sm: 40 }, color: '#4caf50' }} />
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                Based on 30 days
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="caption">
                                        Categories
                                    </Typography>
                                    <Typography variant={isMobile ? 'h6' : 'h5'}>
                                        {filteredCategories.length}
                                    </Typography>
                                </Box>
                                <TrendingDown sx={{ fontSize: { xs: 30, sm: 40 }, color: '#ff9800' }} />
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                Active categories
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Budget Alert */}
            {isOverBudget && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: '#ffebee', color: '#c62828' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Warning sx={{ mr: 1 }} />
                        <Typography variant="body1">
                            ⚠️ You have exceeded your monthly budget by KES {(filteredTotal - budget).toLocaleString()}!
                        </Typography>
                    </Box>
                </Paper>
            )}

            {/* Charts */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, height: 400 }}>
                        <Typography variant="h6" gutterBottom>
                            Monthly Spending Trend {sourceFilter !== 'all' && `(${getSourceLabel(sourceFilter).label})`}
                        </Typography>
                        <ResponsiveContainer width="100%" height="90%">
                            <AreaChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <RechartsTooltip
                                    formatter={(value) => `KES ${value.toLocaleString()}`}
                                />
                                <Legend />
                                <Area
                                    type="monotone"
                                    dataKey="total"
                                    stroke="#8884d8"
                                    fill="#8884d8"
                                    fillOpacity={0.3}
                                    name="Total Spending"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, height: 400 }}>
                        <Typography variant="h6" gutterBottom>
                            Category Breakdown {sourceFilter !== 'all' && `(${getSourceLabel(sourceFilter).label})`}
                        </Typography>
                        {pieData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="90%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={false}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip
                                        formatter={(value, name) => {
                                            const total = pieData.reduce((sum, d) => sum + d.value, 0);
                                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                            return [`KES ${value.toLocaleString()} (${percentage}%)`, name];
                                        }}
                                    />
                                    <Legend
                                        layout="horizontal"
                                        verticalAlign="bottom"
                                        align="center"
                                        wrapperStyle={{
                                            paddingTop: '10px',
                                            fontSize: '12px',
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography color="textSecondary">No data available</Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Source Distribution Chart */}
            {sourceFilter === 'all' && sourceDistribution.length > 0 && (
                <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Source Distribution
                    </Typography>
                    <Box sx={{ height: 250 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={sourceDistribution}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <RechartsTooltip
                                    formatter={(value) => `KES ${value.toLocaleString()}`}
                                />
                                <Legend />
                                <Bar dataKey="value" name="Total Amount">
                                    {sourceDistribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>
            )}

            {/* Recent Expenses */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Recent Expenses {sourceFilter !== 'all' && `(${getSourceLabel(sourceFilter).label})`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                            size="small"
                            label={`${filteredExpenses.length} transactions`}
                            sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
                        />
                        {useCustomLimit && customLimit && (
                            <Chip
                                size="small"
                                label={`Limit: ${customLimit}`}
                                sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}
                            />
                        )}
                    </Box>
                </Box>
                {recentExpenses.length === 0 ? (
                    <Typography color="textSecondary">No recent expenses</Typography>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '8px' }}>Title</th>
                                    <th style={{ textAlign: 'left', padding: '8px' }}>Category</th>
                                    <th style={{ textAlign: 'left', padding: '8px' }}>Source</th>
                                    <th style={{ textAlign: 'left', padding: '8px' }}>Date</th>
                                    <th style={{ textAlign: 'right', padding: '8px' }}>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentExpenses.map((expense) => {
                                    const source = getSourceLabel(expense.source);
                                    return (
                                        <tr key={expense.id}>
                                            <td style={{ padding: '8px' }}>{expense.title}</td>
                                            <td style={{ padding: '8px' }}>
                                                <Chip label={expense.category} size="small" />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <Chip
                                                    icon={source.icon}
                                                    label={source.label}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: source.color + '20',
                                                        color: source.color,
                                                        borderColor: source.color,
                                                    }}
                                                    variant="outlined"
                                                />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                {format(new Date(expense.date), 'dd MMM yyyy')}
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>
                                                KES {parseFloat(expense.amount).toLocaleString()}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Box>
                )}
            </Paper>

            {/* Budget Dialog */}
            <Dialog open={budgetDialogOpen} onClose={() => setBudgetDialogOpen(false)}>
                <DialogTitle>Set Monthly Budget</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Budget Amount (KES)"
                        type="number"
                        fullWidth
                        value={newBudget}
                        onChange={(e) => setNewBudget(e.target.value)}
                        placeholder="Enter your monthly budget"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBudgetDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSetBudget} variant="contained" sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}>
                        Save Budget
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Dashboard;